import { useState, useCallback } from "react";
import ModalWrapper from "./ModalWrapper";
import { InputForm } from "../form/input/InputForm";
import { changeMyPassword } from "src/apis/userApi";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useUserStore from "src/store/useUserStore";
import { resetAllColumnStates } from "src/utils/grid/resetAllColumnStates";

interface PasswordChangeModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const PasswordChangeModal = ({ isOpen, onClose }: PasswordChangeModalProps) => {
	const [formData, setFormData] = useState({
		currentPassword: '',
		newPassword: '',
		newPasswordCheck: ''
	});
	const [errors, setErrors] = useState({
		currentPassword: '',
		newPassword: '',
		newPasswordCheck: ''
	});

	const { addToast, setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { userInfo } = useUserStore();
	const [activeTab, setActiveTab] = useState<'password' | 'profile' | 'settings'>('password');

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
		// 입력 시 에러 메시지 초기화
		if (errors[name as keyof typeof errors]) {
			setErrors(prev => ({ ...prev, [name]: '' }));
		}
	}, [errors]);

	const validateForm = useCallback((): boolean => {
		const newErrors = {
			currentPassword: '',
			newPassword: '',
			newPasswordCheck: ''
		};

		if (!formData.currentPassword.trim()) {
			newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
		}

		if (!formData.newPassword.trim()) {
			newErrors.newPassword = '새 비밀번호를 입력해주세요.';
		} else if (formData.currentPassword === formData.newPassword) {
			newErrors.newPassword = '현재 비밀번호와 동일한 비밀번호는 사용할 수 없습니다.';
		}

		if (!formData.newPasswordCheck.trim()) {
			newErrors.newPasswordCheck = '비밀번호 확인이 필요합니다.';
		} else if (formData.newPassword !== formData.newPasswordCheck) {
			newErrors.newPasswordCheck = '새 비밀번호와 일치하지 않습니다.';
		}

		setErrors(newErrors);
		return !Object.values(newErrors).some(error => error !== '');
	}, [formData]);

	const handleSubmit = useCallback(async () => {
		if (!validateForm()) {
			return;
		}

		try {
			setLoadingSpinner(true);
			await changeMyPassword(formData.currentPassword, formData.newPassword);
			
			addToast('success', '비밀번호가 성공적으로 변경되었습니다.');
			
			// 폼 초기화
			setFormData({
				currentPassword: '',
				newPassword: '',
				newPasswordCheck: ''
			});
			setErrors({
				currentPassword: '',
				newPassword: '',
				newPasswordCheck: ''
			});
			
			onClose();
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || '비밀번호 변경에 실패했습니다.';
			if (errorMessage.includes('현재 비밀번호') || errorMessage.includes('틀렸')) {
				setErrors(prev => ({ ...prev, currentPassword: '현재 비밀번호가 일치하지 않습니다.' }));
			} else {
				addToast('error', errorMessage);
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [formData, validateForm, onClose, addToast, setLoadingSpinner]);

	const handleClose = useCallback(() => {
		setFormData({
			currentPassword: '',
			newPassword: '',
			newPasswordCheck: ''
		});
		setErrors({
			currentPassword: '',
			newPassword: '',
			newPasswordCheck: ''
		});
		onClose();
	}, [onClose]);

	// 전체 그리드 컬럼 초기화 (모든 화면의 컬럼 순서/너비/표시 상태를 기본값으로 복원)
	const handleResetAllColumns = useCallback(() => {
		const closeConfirm = () => {
			setConfirmModalVisible(false);
			setConfirmModalProps(undefined);
		};

		setConfirmModalProps({
			title: '전체 컬럼 초기화',
			message: '모든 화면의 그리드 컬럼 설정(순서·너비·표시 상태)이\n기본값으로 초기화됩니다.\n적용을 위해 화면이 새로고침됩니다. 진행하시겠습니까?',
			confirmText: '초기화',
			cancelText: '취소',
			onConfirm: () => {
				const count = resetAllColumnStates();
				closeConfirm();
				addToast('success', `그리드 컬럼 설정 ${count}건이 초기화되었습니다.`);
				// 토스트 노출 후 새로고침하여 열려있는 모든 그리드에 반영
				setTimeout(() => window.location.reload(), 300);
			},
			onCancel: closeConfirm,
		});
		setConfirmModalVisible(true);
	}, [addToast, setConfirmModalProps, setConfirmModalVisible]);

	if (!isOpen) return null;

	return (
		<ModalWrapper
			size="sm"
			width={800}
			overlay={true}
			onOverlayClick={handleClose}
			onEscKeyDown={handleClose}
		>
			<div className="w-full h-full flex flex-col">
				{/* 헤더 + 탭 */}
				<div className="w-full h-[15%] flex flex-col border-b bg-gray-100">
					<div className="flex items-center justify-between px-[24px] h-1/2">
						<p className="font-bold text-lg">비밀번호 / 계정 설정</p>
						<button
							onClick={handleClose}
							className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
						>
							×
						</button>
					</div>
					<div className="flex px-[24px] h-1/2 gap-[8px] border-t bg-white">
						<button
							type="button"
							className={`px-3 text-sm border-b-2 ${
								activeTab === 'password'
									? 'border-orange-500 text-orange-600 font-semibold'
									: 'border-transparent text-gray-500 hover:text-gray-700'
							}`}
							onClick={() => setActiveTab('password')}
						>
							비밀번호 변경
						</button>
						<button
							type="button"
							className={`px-3 text-sm border-b-2 ${
								activeTab === 'profile'
									? 'border-orange-500 text-orange-600 font-semibold'
									: 'border-transparent text-gray-500 hover:text-gray-700'
							}`}
							onClick={() => setActiveTab('profile')}
						>
							프로필 정보
						</button>
						<button
							type="button"
							className={`px-3 text-sm border-b-2 ${
								activeTab === 'settings'
									? 'border-orange-500 text-orange-600 font-semibold'
									: 'border-transparent text-gray-500 hover:text-gray-700'
							}`}
							onClick={() => setActiveTab('settings')}
						>
							환경 설정
						</button>
					</div>
				</div>

				{/* 본문: 탭별 컨텐츠 */}
				<div className="flex-1 flex flex-col items-center justify-center px-[40px] py-[20px] gap-[20px]">
					{activeTab === 'password' && (
						<>
							{/* 사용자 정보 */}
							<div className="w-full flex flex-col items-center mb-[20px]">
								<div className="w-[80px] h-[80px] rounded-full bg-orange-400 flex items-center justify-center mb-[10px]">
									<span className="text-white text-4xl">👤</span>
								</div>
								<p className="text-lg font-semibold">
									반갑습니다! {userInfo?.userNm} 님
								</p>
							</div>

							{/* 비밀번호 입력 폼 */}
							<div className="w-full flex flex-col gap-[15px]">
								<div className="w-full">
									<InputForm
										title="현재 비밀번호"
										value={formData.currentPassword}
										editable={true}
										name="currentPassword"
										onChange={handleChange}
										type="password"
										maxLength={100}
										placeholder="현재 비밀번호"
										wrapperOption={{
											wrapperStyle: "w-full mb-[5px]",
											titleStyle: "w-[150px] min-w-[150px]",
											inputStyle: "flex-1"
										}}
									/>
									{errors.currentPassword && (
										<p className="text-red-500 text-sm ml-[160px] mt-[-5px]">{errors.currentPassword}</p>
									)}
								</div>

								<div className="w-full">
									<InputForm
										title="새 비밀번호"
										value={formData.newPassword}
										editable={true}
										name="newPassword"
										onChange={handleChange}
										type="password"
										maxLength={100}
										placeholder="새 비밀번호"
										wrapperOption={{
											wrapperStyle: "w-full mb-[5px]",
											titleStyle: "w-[150px] min-w-[150px]",
											inputStyle: "flex-1"
										}}
									/>
									{errors.newPassword && (
										<p className="text-red-500 text-sm ml-[160px] mt-[-5px]">{errors.newPassword}</p>
									)}
								</div>

								<div className="w-full">
									<InputForm
										title="새 비밀번호 확인"
										value={formData.newPasswordCheck}
										editable={true}
										name="newPasswordCheck"
										onChange={handleChange}
										type="password"
										maxLength={100}
										placeholder="새 비밀번호 확인"
										wrapperOption={{
											wrapperStyle: "w-full mb-[5px]",
											titleStyle: "w-[150px] min-w-[150px]",
											inputStyle: "flex-1"
										}}
									/>
									{errors.newPasswordCheck && (
										<p className="text-red-500 text-sm ml-[160px] mt-[-5px]">{errors.newPasswordCheck}</p>
									)}
								</div>
							</div>
						</>
					)}

					{activeTab === 'profile' && (
						<div className="w-full flex flex-col items-center gap-[12px]">
							<div className="w-[80px] h-[80px] rounded-full bg-orange-400 flex items-center justify-center mb-[10px]">
								<span className="text-white text-4xl">👤</span>
							</div>
							<p className="text-lg font-semibold">{userInfo?.userNm}</p>
							<p className="text-sm text-gray-600">아이디: {userInfo?.userId}</p>
						</div>
					)}

					{activeTab === 'settings' && (
						<div className="w-full flex flex-col gap-[12px]">
							<div className="w-full flex items-center justify-between border rounded-md px-[16px] py-[14px]">
								<div className="flex flex-col">
									<p className="text-sm font-semibold text-gray-800">전체 컬럼 초기화</p>
									<p className="text-xs text-gray-500 mt-[4px]">
										모든 화면의 그리드 컬럼 순서·너비·표시 상태를 기본값으로 되돌립니다.
									</p>
								</div>
								<button
									type="button"
									onClick={handleResetAllColumns}
									className="px-4 py-2 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap"
								>
									초기화
								</button>
							</div>
						</div>
					)}
				</div>

				{/* 푸터 - 비밀번호 탭에서만 저장 버튼 노출 */}
				<div className="w-full h-[15%] flex justify-end items-center gap-[8px] px-[24px] border-t bg-gray-50">
					<button
						onClick={handleClose}
						className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
					>
						취소
					</button>
					{activeTab === 'password' && (
						<button
							onClick={handleSubmit}
							className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
						>
							패스워드 변경
						</button>
					)}
				</div>
			</div>
		</ModalWrapper>
	);
};

